/**
 * InsertUndoToast — G4D CP3.
 *
 * Compact top-right countdown toast rendered by `CoachPanel.jsx` after
 * a successful Coach-suggestion insert. Live countdown from the
 * `undo_expires_at` timestamp; clicking Undo fires DELETE
 * `/api/proposals/{id}/section-edits/{audit_id}`.
 *
 * Behaviour:
 *   * Only ONE toast is ever mounted (enforced by the parent).
 *   * Countdown reaches 0 → toast auto-dismisses.
 *   * On 200 undo → onUndone fires (parent re-fetches section content).
 *   * On 410 undo_expired / 410 already_undone → banner switches to
 *     "Undo period expired" for 3s, then dismisses.
 *   * On 409 section_modified_after_insert → banner switches to
 *     "Section was edited after insert — undo unavailable" for 3s.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Loader2, RotateCcw, X } from "lucide-react";

import { api } from "@/api/client";
import {
  INSERT_UNDO_ALREADY_UNDONE,
  INSERT_UNDO_CONFIRMATION,
  INSERT_UNDO_EXPIRED,
  INSERT_UNDO_LABEL,
  INSERT_UNDO_SECTION_MODIFIED,
  INSERT_UNDO_TOAST_PREFIX,
  INSERT_UNDO_TOAST_SUFFIX,
  INSERT_UNDO_VIEW_SECTION_LABEL,
} from "./coach_copy";


function pad(n) { return String(n).padStart(2, "0"); }


export default function InsertUndoToast({
  proposalId,
  auditId,
  sectionTitle,
  undoExpiresAt,   // ISO string
  onUndone,        // ({audit_id, section_key}) => void — parent should refresh section
  onDismiss,
}) {
  const expiresAtMs = useMemo(
    () => new Date(undoExpiresAt).getTime(),
    [undoExpiresAt],
  );
  const [remaining, setRemaining] = useState(
    Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000)),
  );
  const [status, setStatus] = useState("idle"); // idle | undoing | confirmed | expired | error
  const [errorMsg, setErrorMsg] = useState(null);
  const mountedRef = useRef(true);

  // StrictMode-safe: re-assert `true` on setup so React 18's dev
  // double-mount doesn't leave the ref stuck at `false`.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Live countdown.
  useEffect(() => {
    if (status !== "idle") return undefined;
    const tick = () => {
      const nextRemaining = Math.max(
        0,
        Math.ceil((expiresAtMs - Date.now()) / 1000),
      );
      setRemaining(nextRemaining);
      if (nextRemaining <= 0) {
        setStatus("expired");
        setErrorMsg(INSERT_UNDO_EXPIRED);
      }
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [expiresAtMs, status]);

  // Auto-dismiss after a terminal state settles.
  useEffect(() => {
    if (status !== "confirmed" && status !== "expired" && status !== "error") {
      return undefined;
    }
    const t = setTimeout(() => onDismiss?.(), status === "confirmed" ? 2000 : 3000);
    return () => clearTimeout(t);
  }, [status, onDismiss]);

  const undo = useCallback(async () => {
    setStatus("undoing");
    setErrorMsg(null);
    try {
      const res = await api.delete(
        `/proposals/${proposalId}/section-edits/${auditId}`,
      );
      if (!mountedRef.current) return;
      onUndone?.({
        audit_id: res.data.audit_id,
        section_key: res.data.section_key,
      });
      setStatus("confirmed");
    } catch (err) {
      if (!mountedRef.current) return;
      const httpStatus = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (httpStatus === 410 && detail?.code === "undo_expired") {
        setStatus("expired");
        setErrorMsg(INSERT_UNDO_EXPIRED);
      } else if (httpStatus === 410 && detail?.code === "already_undone") {
        setStatus("expired");
        setErrorMsg(INSERT_UNDO_ALREADY_UNDONE);
      } else if (httpStatus === 409 && detail?.code === "section_modified_after_insert") {
        setStatus("error");
        setErrorMsg(INSERT_UNDO_SECTION_MODIFIED);
      } else {
        setStatus("error");
        setErrorMsg(INSERT_UNDO_EXPIRED);
      }
    }
  }, [proposalId, auditId, onUndone]);

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;

  if (status === "confirmed") {
    return (
      <div
        data-testid="insert-undo-toast"
        className="fixed top-4 right-4 z-40 rounded-2xl px-4 py-3 flex items-start gap-3 max-w-sm shadow-lg"
        style={{
          background: "var(--v2-surface, #14141c)",
          border: "1px solid rgba(52,211,153,0.5)",
          color: "var(--v2-text, #EDEDE8)",
        }}
      >
        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#0F9F70" }} />
        <p data-testid="insert-undo-toast-confirmation" className="text-v2-body-sm leading-relaxed">
          {INSERT_UNDO_CONFIRMATION}
        </p>
      </div>
    );
  }

  if (status === "expired" || status === "error") {
    return (
      <div
        data-testid="insert-undo-toast"
        className="fixed top-4 right-4 z-40 rounded-2xl px-4 py-3 flex items-start gap-3 max-w-sm shadow-lg"
        style={{
          background: "var(--v2-surface, #14141c)",
          border: "1px solid rgba(148,163,184,0.45)",
          color: "var(--v2-textMuted, #94a3b8)",
        }}
      >
        <p data-testid="insert-undo-toast-terminal" className="text-v2-caption leading-relaxed">
          {errorMsg || INSERT_UNDO_EXPIRED}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="insert-undo-toast"
      className="fixed top-4 right-4 z-40 rounded-2xl px-4 py-3 flex flex-col gap-2 max-w-sm shadow-lg"
      style={{
        background: "var(--v2-surface, #14141c)",
        border: "1px solid rgba(167,139,250,0.45)",
        color: "var(--v2-text, #EDEDE8)",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-v2-body-sm leading-relaxed">
          {INSERT_UNDO_TOAST_PREFIX} <span
            data-testid="insert-undo-toast-section-title"
            className="font-semibold"
          >{sectionTitle}</span>{INSERT_UNDO_TOAST_SUFFIX}
        </p>
        <button
          type="button"
          data-testid="insert-undo-toast-dismiss"
          onClick={onDismiss}
          className="inline-flex items-center justify-center rounded-full w-6 h-6 text-v2-textMuted hover:text-v2-text hover:bg-v2-surfaceSubtle transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex items-center gap-3 justify-between">
        <button
          type="button"
          data-testid="insert-undo-toast-button"
          disabled={status === "undoing"}
          onClick={undo}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-v2-caption font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            background: "rgba(167,139,250,0.14)",
            color: "var(--v2-accent, #5D50C8)",
            border: "1px solid rgba(167,139,250,0.35)",
          }}
        >
          {status === "undoing" ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RotateCcw className="w-3.5 h-3.5" />
          )}
          {INSERT_UNDO_LABEL}
        </button>
        <span
          data-testid="insert-undo-toast-countdown"
          className="text-v2-caption text-v2-textMuted font-mono tabular-nums"
        >
          {pad(minutes)}:{pad(seconds)}
        </span>
      </div>
      {/* "View updated section" — closes the UX gap on wizard step 10
          where the CoachPanel is mounted but the section content itself
          isn't rendered. Sending the user to `/proposals/{id}/draft`
          lets them SEE the change without hunting for it. Rendered
          only in the idle (pre-terminal) state — after undo confirms
          or the window expires it becomes irrelevant. */}
      <Link
        to={`/proposals/${proposalId}/draft`}
        data-testid="insert-undo-toast-view-section"
        className="inline-flex items-center gap-1 text-v2-caption text-v2-textMuted hover:text-v2-accent transition-colors"
      >
        {INSERT_UNDO_VIEW_SECTION_LABEL}
        <ArrowUpRight className="w-3 h-3" />
      </Link>
    </div>
  );
}
